import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { trimValidator } from '../../helpers';
import { Channel } from '../../modules/crm/channel/components/channel/channel.interface';
import { ChannelService } from '../../services/channel.service';
import {
  CreateChatForMessagePayload,
  IWriteMessageForm,
  PHONE_FIELD_CHANNEL_TYPES,
  REQUIRED_ALL_FIELDS_CHANNEL_TYPES,
  SEND_MESSAGE_CHANNEL_TYPES,
  SOME_FIELD_REQUIRED_CHANNEL_TYPES,
  USERNAME_FIELD_CHANNEL_TYPES,
} from './write-message.interface';
import { CHANNEL_STATUS, CHANNEL_TYPE, CHANNEL_TYPES_OBJECT, activeTranslations, PHONES_FOR_INPUT } from '../../enum/constants';
import { Subject } from 'rxjs';
import { NotificationService } from '../../services/notifications.service';
import { phoneValidator } from '../../validators/phone.validator';
import { ChatService } from '../../services/chat.service';
import { MatDialogRef } from '@angular/material/dialog';
import { someFieldRequired } from '../../validators/some-field-required.validator';
import { RequestHelper } from '../../services/request-helper.service';

@Component({
  selector: 'app-write-message',
  templateUrl: './write-message.component.html',
  styleUrls: ['./write-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WriteMessageComponent implements OnInit, OnDestroy {
  constructor(
    private dialogRef: MatDialogRef<WriteMessageComponent>,
    private readonly requestHelper: RequestHelper,
    protected readonly channelService: ChannelService,
    protected readonly notificationService: NotificationService,
    protected readonly chatService: ChatService,
  ) {}

  private readonly destroy$ = new Subject<void>();

  protected channel?: Channel;
  protected channels: Channel[] = [];

  protected formGroup!: FormGroup<IWriteMessageForm>;
  protected isShowSendMessage: boolean = false;
  protected loaders = {
    default: false,
  };

  ngOnInit(): void {
    this.initFormGroup();
    this.initChannels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected writeMessage() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const requestBody: CreateChatForMessagePayload = {
      channel_id: this.channel!.id,
      chat_name: this.formGroup.controls.chat_name.value as string,
    };

    if (this.formGroup.controls.phone?.value) {
      requestBody.phone = this.formGroup.controls.phone.value;
    }

    if (this.formGroup.controls.username?.value) {
      requestBody.username = this.formGroup.controls.username.value;
    }

    const channelNumber = (this?.channel?.x_data['number'] || this?.channel?.x_data['phone_number']).replace('+', '');

    if (channelNumber && channelNumber === requestBody?.phone?.replace('+', '')) {
      this.notificationService.showError(
        activeTranslations.notifications_texts.error.error,
        activeTranslations.notifications_texts.error.you_cannot_write_message_yourself,
      );
      return;
    }
    this.requestHelper.handle<string>(
      this.chatService.createChatForMessage(requestBody),
      (chatId: string) => {
        this.moveToChat(chatId);
      },
      {
        key: 'write_message_create_chat_for_message',
        loaderRef: this.loaders,
        loaderKey: 'default',
        destroy$: this.destroy$,
        defaultErrorMessage: 'notifications_texts.error.failed_to_create_chat',
      },
    );
  }

  private initFormGroup() {
    this.formGroup = new FormGroup({
      channel_id: new FormControl<string | null>(null, [Validators.required, trimValidator]),
      chat_name: new FormControl<string | null>(null, [Validators.required, trimValidator]),
    });
  }

  private initChannels() {
    this.requestHelper.handle<Channel[]>(
      this.channelService.getChannels({
        scope: 'chats',
        types: [CHANNEL_TYPE.VIBER, CHANNEL_TYPE.TELEGRAM],
        status: CHANNEL_STATUS.CONNECTED,
      }),
      (channels: Channel[]) => {
        if (!channels?.length) return;

        channels.forEach((item: any) => {
          item['img_url'] = CHANNEL_TYPES_OBJECT[item.type as CHANNEL_TYPE]?.img_url;
        });
        this.channels = channels;
      },
      {
        key: 'write_message_get_channels',
        loaderRef: this.loaders,
        loaderKey: 'default',
        destroy$: this.destroy$,
        defaultErrorMessage: 'notifications_texts.error.failed_to_create_chat',
      },
    );
  }

  private resetChannel(): void {
    this.channel = undefined;
    this.isShowSendMessage = false;

    Object.keys(this.formGroup.controls).forEach((controlName) => {
      if (!['chat_name', 'channel_id'].includes(controlName)) {
        this.formGroup.removeControl(controlName as any);
      }
    });
    this.formGroup.clearValidators();
    this.formGroup.updateValueAndValidity();
  }

  protected channelSelected(): void {
    this.resetChannel();

    const channelId = this.formGroup.controls.channel_id.value;

    if (!channelId) return;

    this.requestHelper.handle<Channel>(
      this.channelService.getChannelById(channelId),
      (channel: Channel) => {
        this.channel = channel;

        this.isShowSendMessage = SEND_MESSAGE_CHANNEL_TYPES.includes(channel.type);

        this.addFormGroupControls();
      },
      {
        key: 'write_message_get_channel_by_id',
        loaderRef: this.loaders,
        loaderKey: 'default',
        destroy$: this.destroy$,
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_channel',
      },
    );
  }

  private addFormGroupControls(): void {
    const channelType = this.channel!.type;

    if (PHONE_FIELD_CHANNEL_TYPES.includes(channelType)) {
      this.formGroup.addControl('phone', new FormControl<string | null>(null, [phoneValidator]));
    }

    if (USERNAME_FIELD_CHANNEL_TYPES.includes(channelType)) {
      this.formGroup.addControl('username', new FormControl<string | null>(null));
    }

    if (REQUIRED_ALL_FIELDS_CHANNEL_TYPES.includes(channelType)) {
      Object.keys(this.formGroup.controls).forEach((controlName: string) => {
        if (!['chat_name', 'channel_id'].includes(controlName)) {
          this.formGroup.controls[controlName as keyof IWriteMessageForm]?.addValidators(Validators.required);
          this.formGroup.controls[controlName as keyof IWriteMessageForm]?.updateValueAndValidity();
        }
      });
    } else if (SOME_FIELD_REQUIRED_CHANNEL_TYPES.includes(channelType)) {
      this.formGroup.addValidators(someFieldRequired(['chat_name', 'channel_id']));
      this.formGroup.updateValueAndValidity();
    }
  }

  private moveToChat(chatId: string) {
    if (this.chatService.chat?.id !== chatId) {
      this.chatService.selectChat(chatId);
    }

    this.dialogRef.close();
  }

  protected readonly PHONES_FOR_INPUT = PHONES_FOR_INPUT;
}
